import clsx from 'clsx'
import { css, html, nothing, TemplateResult, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import {
  FeedbackType,
  FileItemTemplate,
  FileType,
  LinkTarget,
} from '@/common/types'
import { JSONAttributeParser } from '@/common/utils'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { t } from '@/localization/localization'

import styles from './file-input-list-item.scss?inline'

export type FileListElement = {
  name: string
  type: string
  size: number
  lastModified: number
}

/**
 * `dap-ds-file-input-list-item`
 * @summary A file input list item is a container for file input list items.
 * @element dap-ds-file-input-list-item
 * @title - File input list item
 * @group file-input
 *
 * @event {{ file: FileListElement }} dds-file-remove - Dispatched when a file is removed from the list.
 * @event {{ file: FileListElement }} dds-file-click - Dispatched when a file is clicked.
 *
 * @slot delete-button - The delete button.
 *
 * @csspart base - The main file input list item container.
 * @csspart feedback - The feedback container.
 * @csspart link - The link text of the item component.
 * @csspart filename - The filename text of the item component.
 * @csspart filesize - The filesize text of the item component.
 * @csspart spinner - The spinner of the item component.
 * @csspart remove-button - The remove button of the item component.
 * @csspart thumbnail - The thumbnail of the item component.
 *
 * @cssproperty --dds-file-list-item-padding - The padding of the file list item (default: var(--dds-spacing-200))
 * @cssproperty --dds-file-list-item-border-width - The border width of the file list item (default: var(--dds-border-width-base))
 * @cssproperty --dds-file-list-item-border-color - The border color of the file list item (default: var(--dds-border-neutral-transparent))
 * @cssproperty --dds-file-list-item-border-radius - The border radius of the file list item (default: var(--dds-radius-base))
 * @cssproperty --dds-file-list-item-bg-color - The background color of the file list item (default: var(--dds-fields-background-default))
 * @cssproperty --dds-file-list-item-font-weight - The font weight of the file list item (default: var(--dds-font-weight-medium))
 * @cssproperty --dds-file-list-item-disabled-bg-color - The background color of the disabled file list item (default: var(--dds-cold-grey-400))
 * @cssproperty --dds-file-list-item-disabled-color - The text color of the disabled file list item (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-file-list-item-content-margin - The margin of the content within the file list item (default: var(--dds-spacing-100) var(--dds-spacing-200))
 * @cssproperty --dds-file-list-item-content-gap - The gap between content elements in the file list item (default: var(--dds-spacing-200))
 * @cssproperty --dds-file-list-item-filename-color - The color of the filename text (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-file-list-item-filesize-color - The color of the filesize text (default: var(--dds-text-neutral-subtle))
 * @cssproperty --dds-file-list-item-feedback-padding-x - The padding of the feedback section (default: var(--dds-spacing-200))
 * @cssproperty --dds-file-list-item-feedback-padding-y - The padding of the feedback section (default: var(--dds-spacing-100))
 * @cssproperty --dds-file-list-item-feedback-border-color - The border color of the feedback section (default: var(--dds-border-neutral-divider))
 * @cssproperty --dds-file-list-item-feedback-font-size - The font size of the feedback text (default: var(--dds-font-sm))
 */
@customElement('dap-ds-file-input-list-item')
export default class DapDSFileInputListItem extends SizedMixin(DdsElement) {
  /** Disables the file input list item. */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** The name of the file input. */
  @property({ type: String }) inputName: string

  /** The feedback message. */
  @property({ type: String }) feedback: string

  /** Feedback type.
   * @type {'negative' | 'positive' | 'warning' | 'info'}
   */
  @property({ type: String }) feedbackType: FeedbackType

  /** Loading state of the file input list item. */
  @property({ type: Boolean, reflect: true }) loading? = false

  /** Show delete button. */
  @property({ type: String }) showDeleteButton: string = 'true'

  /** Show file size. */
  @property({ type: String }) showFileSize? = 'true'

  /** Show file link. */
  @property({ type: String }) showFileLink? = 'true'

  /** Whether to emit a custom event with the file data when the file is clicked. */
  @property({ type: Boolean }) emitLinkEvent: boolean = false

  /** File item template function
   * @type {(fileItemTemplate: FileItemTemplate) => TemplateResult}
   */
  @property({ attribute: false }) fileItemTemplate?: (
    fileItemTemplate: FileItemTemplate,
  ) => TemplateResult

  /** The label of the file link. */
  @property({ type: String }) fileLinkLabel: string

  /** The file data. */
  @property({ type: Object, converter: JSONAttributeParser })
  fileData: FileListElement | File

  /** Subtle style. */
  @property({ type: Boolean }) subtle = false

  /** The progress of the file upload. */
  @property({ type: String }) progress = '0'

  /** The url of the item link. If present, the item title will rendered as a link. */
  @property({ type: String }) href: string

  /** The rel of the item link. Only used if href is present. */
  @property({ type: String }) rel: string

  /** The target of the item link. Only used if href is present. */
  @property({ type: String }) target: LinkTarget

  /** Whether the file should be downloaded when clicked. */
  @property({ type: String }) download: string = 'true'

  /** Whether to show thumbnail preview for supported file types. */
  @property({ type: String }) showThumbnail: string = 'false'

  /** Size of the thumbnail preview. */
  @property({ type: String }) thumbnailSize: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' =
    'sm'

  @state() private _fileObjectUrl: string | null = null
  @state() private _thumbnailUrl: string | null = null
  @state()
  private _fileType: FileType = 'unknown'

  static readonly styles = css`
    ${unsafeCSS(styles)}
  `

  delete(file?: FileListElement | File) {
    this.emit('dds-file-remove', { file: file || this.fileData })
  }

  private detectFileType(file: File | FileListElement): FileType {
    const type = file.type.toLowerCase()

    if (type.startsWith('image/')) return 'image'
    if (type.startsWith('video/')) return 'video'
    if (type.startsWith('audio/')) return 'audio'
    if (
      type.startsWith('text/') ||
      type.includes('document') ||
      type.includes('word') ||
      type.includes('excel') ||
      type.includes('powerpoint') ||
      type.includes('presentation') ||
      type.includes('spreadsheet')
    )
      return 'document'

    if (type.includes('pdf')) return 'pdf'

    return 'unknown'
  }

  private getFileTypeIcon(): TemplateResult<1> {
    let icon = 'file-text-line'
    switch (this._fileType) {
      case 'image':
        icon = 'image-line'
        break
      case 'video':
        icon = 'video-line'
        break
      case 'audio':
        icon = 'music-line'
        break
      case 'document':
        icon = 'file-text-line'
        break
      case 'pdf':
        return html`<svg
          class=${clsx(
            'content__file-icon',
            `content__file-icon--${this.thumbnailSize}`,
            'content__file-icon--custom',
          )}
          viewBox="0 0 40 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <mask
            id="mask0_705_82583"
            style="mask-type:alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="40"
            height="56">
            <path
              d="M0 0L25.7574 0C26.553 0 27.3161 0.316071 27.8787 0.87868L39.1213 12.1213C39.6839 12.6839 40 13.447 40 14.2426L40 56L3 56C1.34315 56 0 54.6569 0 53L0 0Z"
              fill="#9F9F9F" />
          </mask>
          <g mask="url(#mask0_705_82583)">
            <path
              d="M0 2C0 0.89543 0.895431 0 2 0H27L40 13V54C40 55.1046 39.1046 56 38 56H2C0.895431 56 0 55.1046 0 54V2Z"
              fill="white" />
            <path
              d="M7.46234 29.3892V28.0122H10.3481C11.061 28.0122 11.6127 27.8291 12.0034 27.4629C12.3989 27.0918 12.5966 26.5791 12.5966 25.9248V25.9102C12.5966 25.2461 12.3989 24.731 12.0034 24.3647C11.6127 23.9985 11.061 23.8154 10.3481 23.8154H7.46234V22.4312H10.7582C11.4516 22.4312 12.0595 22.5776 12.582 22.8706C13.1093 23.1587 13.5219 23.564 13.8198 24.0864C14.1176 24.604 14.2665 25.207 14.2665 25.8955V25.9102C14.2665 26.5986 14.1176 27.2065 13.8198 27.7339C13.5219 28.2563 13.1093 28.6641 12.582 28.957C12.0595 29.2451 11.4516 29.3892 10.7582 29.3892H7.46234ZM6.64203 33V22.4312H8.28266V33H6.64203ZM16.3232 33V22.4312H20.1392C21.189 22.4312 22.085 22.6387 22.8271 23.0537C23.5693 23.4688 24.1357 24.0669 24.5264 24.8481C24.9219 25.6294 25.1196 26.5742 25.1196 27.6826V27.6973C25.1196 28.8154 24.9219 29.77 24.5264 30.561C24.1357 31.3521 23.5693 31.9575 22.8271 32.3774C22.0898 32.7925 21.1938 33 20.1392 33H16.3232ZM17.9639 31.5791H19.9561C20.6934 31.5791 21.3208 31.4277 21.8384 31.125C22.3608 30.8223 22.7563 30.3828 23.0249 29.8066C23.2983 29.2305 23.4351 28.5347 23.4351 27.7192V27.7046C23.4351 26.894 23.2959 26.2007 23.0176 25.6245C22.7441 25.0483 22.3486 24.6089 21.8311 24.3062C21.3135 24.0034 20.6885 23.8521 19.9561 23.8521H17.9639V31.5791ZM27.2789 33V22.4312H33.878V23.8521H28.9195V27.1919H33.4605V28.5762H28.9195V33H27.2789Z"
              fill="#EE442A" />
            <path
              d="M42.5938 13.125H29.5C28.0503 13.125 26.875 11.9497 26.875 10.5V-2.59375L42.5938 13.125Z"
              fill="white"
              stroke="#E0E0E0"
              stroke-width="0.75" />
          </g>
          <path
            d="M2 0.375H25.7578C26.4537 0.37512 27.1211 0.651542 27.6133 1.14355L38.8564 12.3867C39.3485 12.8789 39.6249 13.5463 39.625 14.2422V54C39.625 54.8975 38.8975 55.625 38 55.625H2C1.10254 55.625 0.375 54.8975 0.375 54V2C0.375 1.10254 1.10254 0.375 2 0.375Z"
            stroke="#E0E0E0"
            stroke-width="0.75" />
        </svg> `
      default:
        icon = 'file-text-line'
        break
    }

    return html`<dap-ds-icon
      class=${clsx(
        'content__file-icon',
        `content__file-icon--${this.thumbnailSize}`,
      )}
      name=${icon}></dap-ds-icon>`
  }

  private async generateThumbnail(file: File): Promise<string | null> {
    if (this._fileType === 'image') {
      return new Promise(resolve => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()

        img.onload = () => {
          const maxSize =
            this.thumbnailSize === 'xxs'
              ? 24
              : this.thumbnailSize === 'xs'
                ? 32
                : this.thumbnailSize === 'sm'
                  ? 48
                  : this.thumbnailSize === 'md'
                    ? 64
                    : 80

          const ratio = Math.min(maxSize / img.width, maxSize / img.height)
          canvas.width = img.width * ratio
          canvas.height = img.height * ratio

          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height)
          resolve(canvas.toDataURL('image/jpeg', 0.8))
        }

        img.onerror = () => resolve(null)
        img.src = URL.createObjectURL(file)
      })
    }

    return null
  }

  private async createFileObjectUrl() {
    if (this.fileData) {
      this._fileType = this.detectFileType(this.fileData)

      if (this.fileData instanceof File) {
        if (this._fileObjectUrl && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(this._fileObjectUrl)
        }

        if (typeof URL.createObjectURL === 'function') {
          this._fileObjectUrl = URL.createObjectURL(this.fileData)
        } else {
          console.warn('URL.createObjectURL not available in this environment')
        }

        if (this.showThumbnail === 'true' && this._fileType === 'image') {
          try {
            this._thumbnailUrl = await this.generateThumbnail(this.fileData)
            this.requestUpdate()
          } catch (error) {
            console.warn('Failed to generate thumbnail:', error)
          }
        }
      } else if (typeof this.fileData === 'object' && 'type' in this.fileData) {
        // If it's metadata, we can't create a preview without the actual file content
        console.warn(
          'Cannot create file preview: fileData contains only metadata, not file content',
        )
      }
    }
  }

  cleanupFileObjectUrl() {
    if (this._fileObjectUrl && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this._fileObjectUrl)
      this._fileObjectUrl = null
    }
    if (this._thumbnailUrl) {
      // Thumbnail URLs are data URLs, so no need to revoke them
      this._thumbnailUrl = null
    }
  }

  //emit a custom event with the file data}
  private handleFileClick(event: Event) {
    event.preventDefault()

    if (!this.emitLinkEvent) {
      if (this._fileObjectUrl) {
        // Open the file in a new tab for preview
        window.open(this._fileObjectUrl, '_blank')
      } else if (this.href) {
        // Fallback to href if no object URL available
        window.open(this.href, this.target || '_blank')
      } else {
        console.warn('No object URL or href available for file preview')
      }
    } else {
      this.emit('dds-file-click', {
        data: {
          file: this.fileData,
          fileObjectUrl: this._fileObjectUrl,
          href: this.href,
          target: this.target,
          rel: this.rel,
          download: this.download,
          showFileLink: this.showFileLink || '',
          showFileSize: this.showFileSize || '',
          showDeleteButton: this.showDeleteButton || '',
          formattedSize: this.formattedSize,
          feedback: this.feedback,
          feedbackType: this.feedbackType,
          loading: this.loading || false,
          showThumbnail: this.showThumbnail,
          fileType: this._fileType,
          fileLinkLabel: this.fileLinkLabel,
          thumbnailUrl: this._thumbnailUrl,
          thumbnailSize: this.thumbnailSize,
          handleFileClick: this.handleFileClick,
          handleDelete: this.delete,
          component: this,
        },
      })
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.createFileObjectUrl()
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties)

    // Regenerate object URL and thumbnail when fileData changes
    if (changedProperties.has('fileData')) {
      this.cleanupFileObjectUrl()
      this.createFileObjectUrl()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.cleanupFileObjectUrl()
  }

  get formattedSize() {
    const exponent = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const fractionDigits = 0
    const size = this.fileData?.size || 0

    if (!+size) {
      return `0 ${sizes[0]}`
    }

    const i = Math.floor(Math.log(size) / Math.log(exponent))

    return `${parseFloat((size / Math.pow(exponent, i)).toFixed(fractionDigits))} ${sizes[i]}`
  }

  render() {
    return (
      this.fileData &&
      html`
        <li
          part="base"
          aria-live="polite"
          class=${clsx(
            'file-list-item',
            `file-list-item--${this.effectiveSize}`,
            {
              'file-list-item--disabled': this.disabled,
            },
          )}
          aria-label=${`${this.fileData?.name || ''} ${this.loading ? t('fileInput.uploading') : ''} ${this.feedback ? this.feedback : ''}`}>
          ${this.fileItemTemplate
            ? html`<div class="content" part="content">
                ${this.fileItemTemplate({
                  file: this.fileData as File,
                  fileObjectUrl: this._fileObjectUrl || '',
                  href: this.href,
                  target: this.target,
                  rel: this.rel,
                  download: this.download,
                  showFileLink: this.showFileLink || '',
                  showFileSize: this.showFileSize || '',
                  showDeleteButton: this.showDeleteButton || '',
                  formattedSize: this.formattedSize,
                  feedback: this.feedback,
                  feedbackType: this.feedbackType,
                  loading: this.loading || false,
                  showThumbnail: this.showThumbnail,
                  fileType: this._fileType,
                  fileLinkLabel: this.fileLinkLabel,
                  thumbnailUrl: this._thumbnailUrl,
                  thumbnailSize: this.thumbnailSize,
                  handleFileClick: this.handleFileClick,
                  handleDelete: this.delete,
                  component: this,
                })}
              </div>`
            : html`<div class="content" part="content">
                  ${this.showThumbnail === 'true' && this._thumbnailUrl
                    ? html`<img
                        part="thumbnail"
                        class=${clsx(
                          'content__thumbnail',
                          `content__thumbnail--${this.thumbnailSize}`,
                        )}
                        src=${this._thumbnailUrl}
                        alt=${`${this.fileData?.name || ''} thumbnail`}
                        loading="lazy" />`
                    : this.showThumbnail === 'true' &&
                        this._fileType !== 'unknown'
                      ? this.getFileTypeIcon()
                      : nothing}
                  <dap-ds-stack spacing="200">
                    <span part="filename">${this.fileData?.name || ''}</span>
                    ${this.showFileLink === 'true' &&
                    (this._fileObjectUrl || this.href)
                      ? html`<slot name="link">
                          <dap-ds-link
                            part="link"
                            href="${this._fileObjectUrl || this.href}"
                            target=${ifDefined(this.target)}
                            rel=${ifDefined(this.rel)}
                            aria-label=${`${this.fileLinkLabel ? `${this.fileLinkLabel} ${this.fileData?.name || ''}` : `${t('fileInput.view')} ${this.fileData?.name || ''}`}`}
                            ?download=${this.download === 'true'
                              ? true
                              : undefined}
                            @click=${this.handleFileClick}
                            >${this.fileLinkLabel
                              ? this.fileLinkLabel
                              : t('fileInput.view')}</dap-ds-link
                          ></slot
                        >`
                      : nothing}
                  </dap-ds-stack>
                  <span class="content__markers">
                    ${this.showFileSize === 'true'
                      ? html`<span
                          part="filesize"
                          class=${clsx(
                            'content__filesize',
                            `content__filesize--${this.effectiveSize}`,
                          )}
                          aria-label=${t('fileInput.fileSize', {
                            size: this.formattedSize,
                          })}
                          >${this.formattedSize}</span
                        >`
                      : nothing}
                    ${this.loading
                      ? html`<dap-ds-spinner
                          part="spinner"
                          size="sm"
                          type="neutral"
                          aria-label=${t(
                            'fileInput.uploading',
                          )}></dap-ds-spinner>`
                      : nothing}
                    ${this.showDeleteButton === 'true'
                      ? html` <dap-ds-icon-button
                          part="remove-button"
                          class="content__remove-button"
                          ?disabled="${this.disabled}"
                          size=${this.effectiveSize as
                            | 'xs'
                            | 'sm'
                            | 'md'
                            | 'lg'}
                          aria-label=${`${t('fileInput.delete')} ${this.fileData?.name || ''}`}
                          clean
                          @click=${() => this.delete(this.fileData)}>
                          <slot name="delete-button">
                            <dap-ds-icon
                              class="content__remove-button-icon"
                              name="delete-bin-line"></dap-ds-icon>
                          </slot>
                        </dap-ds-icon-button>`
                      : nothing}
                  </span>
                </div>
                ${this.feedback &&
                html`
                  <dap-ds-feedback
                    part="feedback"
                    class="feedback"
                    feedbackSize="sm"
                    feedbackType=${ifDefined(this.feedbackType)}
                    .subtle=${this.subtle}
                    role="alert"
                    aria-live="assertive">
                    <span class="feedback__text">${this.feedback}</span>
                  </dap-ds-feedback>
                `}`}
        </li>
      `
    )
  }
}
